import {
  currentOrPastDateUI,
  fullNameNoSuffixUI,
  fullNameNoSuffixSchema,
  serviceNumberSchema,
  serviceNumberUI,
  ssnOrVaFileNumberNoHintSchema,
  ssnOrVaFileNumberNoHintUI,
  titleUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import VaDateField from 'platform/forms-system/src/js/web-component-fields/VaDateField';
import VaMemorableDateField from 'platform/forms-system/src/js/web-component-fields/VaMemorableDateField';
import {
  currentYear,
  parseISODate,
} from 'platform/forms-system/src/js/helpers';
import { isValidDate } from 'platform/forms-system/src/js/utilities/validations';
import { veteranFields, FORM_21_4502 } from '../definitions/constants';

const { PERSONAL_INFO: P } = FORM_21_4502;
const veteranIdentificationUI = ssnOrVaFileNumberNoHintUI();

const veteranFullNameUI = fullNameNoSuffixUI();
const veteranFullNameSchema = {
  ...fullNameNoSuffixSchema,
  properties: {
    ...fullNameNoSuffixSchema.properties,
    middle: {
      type: 'string',
      maxLength: 1,
    },
  },
};

const validateDateOfBirth = (errors, value) => {
  const { day, month, year } = parseISODate(value);
  const hasAnyDatePart = [day, month, year].some(Boolean);
  const hasCompleteDate = [day, month, year].every(Boolean);

  if (!value || !hasAnyDatePart) {
    errors.addError(P.ERROR_DOB_REQUIRED);
    return;
  }

  if (!hasCompleteDate) {
    errors.addError(P.ERROR_DOB_INCOMPLETE);
    return;
  }

  if (
    Number(year) < 1900 ||
    Number(year) > currentYear ||
    !isValidDate(day, month, year)
  ) {
    errors.addError(P.ERROR_DOB_PATTERN);
  }
};

const buildDateOfBirthUI = ({ DateField, monthSelect = false }) => {
  const dateUI = currentOrPastDateUI({
    title: P.DATE_OF_BIRTH,
    required: () => true,
    errorMessages: {
      required: P.ERROR_DOB_REQUIRED,
      pattern: P.ERROR_DOB_PATTERN,
    },
  });

  return {
    ...dateUI,
    'ui:options': {
      ...(dateUI['ui:options'] || {}),
      ...(monthSelect ? { monthSelect: true } : {}),
    },
    'ui:webComponentField': DateField,
    'ui:errorMessages': {
      required: P.ERROR_DOB_REQUIRED,
      pattern: P.ERROR_DOB_PATTERN,
    },
    'ui:validations': [validateDateOfBirth],
  };
};

const buildPersonalInfoBasicPage = ({
  includeIdentification,
  DateField,
  monthSelect = false,
}) => {
  const veteranProperties = {
    [veteranFields.fullName]: veteranFullNameSchema,
    [veteranFields.dateOfBirth]: { type: 'string' },
  };

  const requiredFields = [veteranFields.fullName, veteranFields.dateOfBirth];

  const veteranUiSchema = {
    [veteranFields.fullName]: {
      ...veteranFullNameUI,
      first: {
        ...veteranFullNameUI.first,
        'ui:title': P.FULL_NAME_LABELS['first or given name'],
        'ui:errorMessages': {
          required: P.ERROR_FIRST_NAME,
        },
      },
      middle: {
        ...veteranFullNameUI.middle,
        'ui:title': P.FULL_NAME_LABELS['middle name'],
      },
      last: {
        ...veteranFullNameUI.last,
        'ui:title': P.FULL_NAME_LABELS['last or family name'],
        'ui:errorMessages': {
          required: P.ERROR_LAST_NAME,
        },
      },
    },
    [veteranFields.dateOfBirth]: buildDateOfBirthUI({
      DateField,
      monthSelect,
    }),
  };

  if (includeIdentification) {
    Object.assign(veteranProperties, {
      ...ssnOrVaFileNumberNoHintSchema.properties,
      [veteranFields.veteranServiceNumber]: serviceNumberSchema,
    });

    requiredFields.push(...ssnOrVaFileNumberNoHintSchema.required);

    Object.assign(veteranUiSchema, {
      ...veteranIdentificationUI,
      [veteranFields.ssn]: {
        ...veteranIdentificationUI[veteranFields.ssn],
        'ui:errorMessages': {
          pattern: P.ERROR_SSN_PATTERN,
          required: P.ERROR_SSN_REQUIRED,
        },
      },
      [veteranFields.vaFileNumber]: {
        ...veteranIdentificationUI[veteranFields.vaFileNumber],
        'ui:title': P.VA_FILE_NUMBER,
      },
      [veteranFields.veteranServiceNumber]: serviceNumberUI(
        P.VETERAN_SERVICE_NUMBER,
      ),
    });
  }
  return {
    uiSchema: {
      ...titleUI(P.TITLE, P.PAGE_DESCRIPTION),
      'ui:options': {
        preserveHiddenData: true,
        forceDivWrapper: true,
      },
      [veteranFields.parentObject]: veteranUiSchema,
    },
    schema: {
      type: 'object',
      properties: {
        [veteranFields.parentObject]: {
          type: 'object',
          properties: veteranProperties,
          required: requiredFields,
        },
      },
    },
  };
};

export const personalInfoBasicNewFlow = buildPersonalInfoBasicPage({
  includeIdentification: false,
  DateField: VaMemorableDateField,
  monthSelect: true,
});

export default buildPersonalInfoBasicPage({
  includeIdentification: true,
  DateField: VaDateField,
});
